/**
 * Agents panel helpers: read the agent roster and steer `nightly.yml`.
 *
 * Two rules keep this safe:
 *  1. The YAML is read with a *tiny* line parser that only understands the
 *     `tasks:` block as this repo writes it (agent / enabled / domain lines).
 *  2. Turning an agent on/off edits **one line** — it never rewrites the file,
 *     so comments, focus texts and the planner block survive untouched.
 */
import { listRepoPaths, readFiles, type RepoConfig } from "./github";

export const NIGHTLY_YML_PATH = "agentic/dev/agents/nightly.yml";
export const NIGHTLY_WORKFLOW = "nightly_agents.yml";

export interface AgentTask {
  agent: string;
  domain?: string;
  /** Consolidator-style tasks list several domains instead of one (`domains:`). */
  domains?: string;
  /** Missing line = enabled (matches tools/nightly_runner.py). */
  enabled: boolean;
}

export interface PlannerBlock {
  enabled: boolean;
  maxItems: number;
}

export interface AgentPrompt {
  name: string;
  path: string;
  description: string;
}

/** Strip a trailing `# comment` from a scalar value (`#` only starts a comment after whitespace). */
function stripComment(value: string): string {
  return value.replace(/\s+#.*$/, "").trim();
}

/** Parse the `tasks:` block of nightly.yml (agent / enabled / domain). */
export function parseTasks(yamlText: string): AgentTask[] {
  const lines = yamlText.replace(/\r\n?/g, "\n").split("\n");
  const start = lines.findIndex((l) => /^tasks:\s*$/.test(l));
  if (start === -1) return [];
  const tasks: AgentTask[] = [];
  let current: AgentTask | null = null;
  for (let i = start + 1; i < lines.length; i++) {
    const line = lines[i];
    if (/^\S/.test(line)) break; // next top-level key (planner:, settings:, …)
    const agentMatch = /^(\s*)-\s*agent:\s*([A-Za-z0-9_-]+)/.exec(line);
    if (agentMatch) {
      current = { agent: agentMatch[2], enabled: true };
      tasks.push(current);
      continue;
    }
    if (!current) continue;
    // A trailing comment is allowed and common (`enabled: true  # …`).
    const enabledMatch = /^\s+enabled:\s*(true|false)\s*(#.*)?$/.exec(line);
    if (enabledMatch) current.enabled = enabledMatch[1] === "true";
    const domainMatch = /^\s+domain:\s*(.+)$/.exec(line);
    if (domainMatch) current.domain = stripComment(domainMatch[1]);
    const domainsMatch = /^\s+domains:\s*(.+)$/.exec(line);
    if (domainsMatch) current.domains = stripComment(domainsMatch[1]);
  }
  return tasks;
}

/** Parse the `planner:` block (enabled / max_items). */
export function parsePlanner(yamlText: string): PlannerBlock {
  const lines = yamlText.replace(/\r\n?/g, "\n").split("\n");
  const start = lines.findIndex((l) => /^planner:\s*$/.test(l));
  if (start === -1) return { enabled: true, maxItems: 1 };
  let enabled = true;
  let maxItems = 1;
  for (let i = start + 1; i < lines.length; i++) {
    const line = lines[i];
    if (/^\S/.test(line)) break;
    const enabledMatch = /^\s+enabled:\s*(true|false)\s*(#.*)?$/.exec(line);
    if (enabledMatch) enabled = enabledMatch[1] === "true";
    const maxMatch = /^\s+max_items:\s*(\d+)/.exec(line);
    if (maxMatch) maxItems = Number(maxMatch[1]);
  }
  return { enabled, maxItems };
}

/**
 * Flip `enabled` for one agent — a targeted single-line edit.
 *
 * Returns the new file text, or null when the agent is not configured.
 * An existing `enabled:` line inside the task block is replaced in place
 * (its indentation is kept); otherwise the line is inserted directly below
 * `- agent: <name>`. Nothing else in the file changes.
 */
export function toggleEnabledLine(yamlText: string, agent: string, enabled: boolean): string | null {
  const eol = yamlText.includes("\r\n") ? "\r\n" : "\n";
  const lines = yamlText.replace(/\r\n?/g, "\n").split("\n");
  const start = lines.findIndex((l) => /^tasks:\s*$/.test(l));
  if (start === -1) return null;

  // Find the task block for `agent`.
  let blockStart = -1;
  let blockEnd = lines.length;
  for (let i = start + 1; i < lines.length; i++) {
    if (/^\S/.test(lines[i])) break; // left the tasks block
    const m = /^(\s*)-\s*agent:\s*([A-Za-z0-9_-]+)/.exec(lines[i]);
    if (!m || m[2] !== agent) continue;
    blockStart = i;
    for (let j = i + 1; j < lines.length; j++) {
      if (/^\s*-\s*agent:/.test(lines[j]) || /^\S/.test(lines[j])) {
        blockEnd = j;
        break;
      }
    }
    break;
  }
  if (blockStart === -1) return null;

  // Replace the task's `enabled:` line and COLLAPSE duplicates. Two things
  // matter here: a trailing comment must not defeat the match (an unmatched
  // line used to be duplicated below — and YAML silently honours only the
  // LAST duplicate, so the toggle appeared to do nothing), and the comment
  // itself is worth keeping.
  const matches: { idx: number; indent: string; comment: string }[] = [];
  for (let i = blockStart + 1; i < blockEnd && i < lines.length; i++) {
    const m = /^(\s*)enabled:\s*(true|false)\s*(#.*)?$/.exec(lines[i]);
    if (m) matches.push({ idx: i, indent: m[1], comment: m[3] ?? "" });
  }
  if (matches.length > 0) {
    // Keep a commented line as the survivor when there is one.
    const survivor = matches.find((m) => m.comment) ?? matches[0];
    lines[survivor.idx] = `${survivor.indent}enabled: ${enabled}${
      survivor.comment ? " " + survivor.comment.trimStart() : ""
    }`;
    for (const m of [...matches].sort((a, b) => b.idx - a.idx)) {
      if (m.idx !== survivor.idx) lines.splice(m.idx, 1);
    }
    return lines.join(eol);
  }
  const indent = /^(\s*)/.exec(lines[blockStart])![1];
  lines.splice(blockStart + 1, 0, `${indent}  enabled: ${enabled}`);
  return lines.join(eol);
}

/** Front-matter `description:` of an .agent.md file (first one wins). */
export function extractDescription(markdown: string): string {
  const match = /^description:\s*"?(.*?)"?\s*$/m.exec(markdown.slice(0, 2000));
  return match ? match[1] : "";
}

/** The agent roster from `.github/agents/*.agent.md`. */
export async function listAgents(cfg: RepoConfig): Promise<AgentPrompt[]> {
  const paths = (await listRepoPaths(cfg)).filter((p) =>
    /^\.github\/agents\/[^/]+\.agent\.md$/.test(p),
  );
  const files = await readFiles(cfg, paths);
  return files
    .map((f) => ({
      name: f.path.split("/").pop()!.replace(/\.agent\.md$/, ""),
      path: f.path,
      description: extractDescription(f.content),
    }))
    .sort((a, b) => a.name.localeCompare(b.name));
}

/** Short explanation of the loop, shown on the panel (mirrors agentic/dev/README.md §1). */
export const LOOP_EXPLAINER = [
  "Weekly (Thursdays 21:30 UTC, off-peak) a run checks whether anything changed. If yes, each enabled agent gathers context and writes draft items into agentic/<domain>/open/ — always as next:human, never approved.",
  "You answer and approve in the Inbox. An item you release with ✎ plan gets a plan file from the implementation planner; you review it, then execute it in VS Code (next:build).",
  "Code never runs here: the loop plans in CI, builds locally. The state field on each item is the human gate.",
];
